import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { pingNotifications } from '../lib/notificationsBus';
import DashboardLayout from '../components/DashboardLayout';
import RequestThread from '../components/RequestThread';
import {
  CalendarDays, DollarSign, Plus, Check, X, Clock, CheckCircle, XCircle, AlertCircle,
  ChevronDown, ChevronRight, RotateCcw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_PILL = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  returned_for_revision: 'bg-orange-100 text-orange-700',
};

const STATUS_ICON = { pending: Clock, approved: CheckCircle, rejected: XCircle, returned_for_revision: RotateCcw };

const STATUS_LABEL = { pending: 'pending', approved: 'approved', rejected: 'rejected', returned_for_revision: 'returned' };

const LEAVE_TYPE_LABEL = { pto: 'PTO / Vacation', sick: 'Sick Leave', emergency: 'Emergency' };

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString(); } catch { return d || '—'; } };

const TimeOffAndAdvances = ({ section = 'both' }) => {
  const { user } = useAuth();
  const isDriver = user?.role === 'driver';
  const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin';

  const [tab, setTab] = useState(section === 'advance' ? 'advance' : 'leave');
  const [leaves, setLeaves] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ leave_type: 'pto', start_date: '', end_date: '', reason: '' });
  const [showAdvanceForm, setShowAdvanceForm] = useState(false);
  const [advanceForm, setAdvanceForm] = useState({ amount: '', reason: '' });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [lRes, aRes] = await Promise.all([
        axios.get(`${API}/leave-requests`, { withCredentials: true }),
        axios.get(`${API}/cash-advances`, { withCredentials: true }),
      ]);
      setLeaves(lRes.data);
      setAdvances(aRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const submitLeave = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/leave-requests`, leaveForm, { withCredentials: true });
      pingNotifications();
      setLeaveForm({ leave_type: 'pto', start_date: '', end_date: '', reason: '' });
      setShowLeaveForm(false);
      await fetchAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit leave request');
    }
  };

  const submitAdvance = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/cash-advances`, { ...advanceForm, amount: parseFloat(advanceForm.amount) }, { withCredentials: true });
      pingNotifications();
      setAdvanceForm({ amount: '', reason: '' });
      setShowAdvanceForm(false);
      await fetchAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit advance request');
    }
  };

  const review = async (kind, id, action) => {
    const note = window.prompt(`Optional note for ${action}:`, '');
    if (note === null) return;
    try {
      const path = kind === 'leave' ? `leave-requests` : `cash-advances`;
      await axios.post(`${API}/${path}/${id}/${action}`, { note }, { withCredentials: true });
      pingNotifications();
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || `Failed to ${action}`);
    }
  };

  if (loading) return <DashboardLayout><div className="p-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading...</div></DashboardLayout>;

  const pendingLeaves = leaves.filter(l => l.status === 'pending');
  const pendingAdvances = advances.filter(a => a.status === 'pending');

  const showLeaveSection = section === 'both' || section === 'leave';
  const showAdvanceSection = section === 'both' || section === 'advance';
  const effectiveTab = section === 'leave' ? 'leave' : section === 'advance' ? 'advance' : tab;

  const pageTitle = section === 'leave'
    ? (isDriver ? 'My Leave' : 'Leave Requests')
    : section === 'advance'
      ? (isDriver ? 'My Advances' : 'Cash Advances')
      : 'Time-Off & Advances';
  const pageSubtitle = section === 'leave'
    ? (isDriver ? 'Request PTO, sick days, or emergency time off. Track approval status here.' : 'Review and approve driver leave requests.')
    : section === 'advance'
      ? (isDriver ? 'Request a cash advance. Approved advances are deducted automatically from your future paychecks until repaid.' : 'Review and approve driver cash advance requests.')
      : (isDriver ? 'Submit leave requests and cash advance requests. Track your status here.' : 'Review and approve driver leave + advance requests.');

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="time-off-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>{pageTitle}</h1>
          <p className="text-base text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{pageSubtitle}</p>
        </div>

        {/* Tabs — only show when section === 'both' */}
        {section === 'both' && (
          <div className="flex items-center gap-1 border-b border-slate-200 mb-6">
            <button
              data-testid="tab-leave"
              onClick={() => setTab('leave')}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${tab === 'leave' ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <CalendarDays className="w-4 h-4" />
              Leave Requests {pendingLeaves.length > 0 && isAdmin && <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded-full">{pendingLeaves.length}</span>}
            </button>
            <button
              data-testid="tab-advance"
              onClick={() => setTab('advance')}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${tab === 'advance' ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <DollarSign className="w-4 h-4" />
              Cash Advances {pendingAdvances.length > 0 && isAdmin && <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded-full">{pendingAdvances.length}</span>}
            </button>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800 flex items-start gap-2" data-testid="form-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
          </div>
        )}

        {showLeaveSection && effectiveTab === 'leave' && (
          <div>
            {isDriver && (
              <div className="mb-4">
                <button data-testid="new-leave-button" onClick={() => setShowLeaveForm(!showLeaveForm)} className="bg-[#002FA7] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#00247D] flex items-center gap-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <Plus className="w-4 h-4" /> Request Leave
                </button>
              </div>
            )}
            {showLeaveForm && isDriver && (
              <form onSubmit={submitLeave} className="bg-white border border-slate-200 rounded-md p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="leave-form">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1">Type *</label>
                  <select data-testid="leave-type" required value={leaveForm.leave_type} onChange={(e) => setLeaveForm({ ...leaveForm, leave_type: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm">
                    <option value="pto">PTO / Vacation</option>
                    <option value="sick">Sick Leave</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>
                <div />
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1">Start Date *</label>
                  <input data-testid="leave-start" required type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1">End Date *</label>
                  <input data-testid="leave-end" required type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1">Reason</label>
                  <textarea data-testid="leave-reason" rows="2" value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div className="md:col-span-2 flex justify-end gap-2">
                  <button type="button" onClick={() => setShowLeaveForm(false)} className="px-3 py-1.5 text-sm text-slate-700">Cancel</button>
                  <button data-testid="submit-leave" type="submit" className="px-4 py-1.5 bg-[#002FA7] text-white rounded-md text-sm font-semibold">Submit</button>
                </div>
              </form>
            )}

            {leaves.length === 0 ? (
              <EmptyState text={isDriver ? 'You have no leave requests yet.' : 'No leave requests yet.'} />
            ) : (
              <div className="space-y-2">
                {leaves.map(l => <LeaveRow key={l.id} l={l} isAdmin={isAdmin} onReview={(action) => review('leave', l.id, action)} onChange={fetchAll} />)}
              </div>
            )}
          </div>
        )}

        {showAdvanceSection && effectiveTab === 'advance' && (
          <div>
            {isDriver && (
              <div className="mb-4">
                <button data-testid="new-advance-button" onClick={() => setShowAdvanceForm(!showAdvanceForm)} className="bg-[#002FA7] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#00247D] flex items-center gap-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <Plus className="w-4 h-4" /> Request Advance
                </button>
              </div>
            )}
            {showAdvanceForm && isDriver && (
              <form onSubmit={submitAdvance} className="bg-white border border-slate-200 rounded-md p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="advance-form">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1">Amount ($) *</label>
                  <input data-testid="advance-amount" required type="number" step="0.01" min="0.01" value={advanceForm.amount} onChange={(e) => setAdvanceForm({ ...advanceForm, amount: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div />
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1">Reason</label>
                  <textarea data-testid="advance-reason" rows="2" value={advanceForm.reason} onChange={(e) => setAdvanceForm({ ...advanceForm, reason: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div className="md:col-span-2 flex justify-end gap-2">
                  <button type="button" onClick={() => setShowAdvanceForm(false)} className="px-3 py-1.5 text-sm text-slate-700">Cancel</button>
                  <button data-testid="submit-advance" type="submit" className="px-4 py-1.5 bg-[#002FA7] text-white rounded-md text-sm font-semibold">Submit</button>
                </div>
              </form>
            )}

            {advances.length === 0 ? (
              <EmptyState text={isDriver ? 'You have no advance requests yet.' : 'No advance requests yet.'} />
            ) : (
              <div className="space-y-2">
                {advances.map(a => <AdvanceRow key={a.id} a={a} isAdmin={isAdmin} onReview={(action) => review('advance', a.id, action)} onChange={fetchAll} />)}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

const EmptyState = ({ text }) => (
  <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center">
    <p className="text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{text}</p>
  </div>
);

const StatusPill = ({ status }) => {
  const Icon = STATUS_ICON[status] || Clock;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${STATUS_PILL[status] || 'bg-slate-100 text-slate-600'}`}>
      <Icon className="w-3 h-3" /> {STATUS_LABEL[status] || status}
    </span>
  );
};

const LeaveRow = ({ l, isAdmin, onReview, onChange }) => {
  const [open, setOpen] = useState(false);
  const isReturned = l.status === 'returned_for_revision';
  return (
    <div data-testid={`leave-${l.id}`} className={`bg-white border rounded-md ${isReturned ? 'border-orange-300' : 'border-slate-200'}`}>
      <div
        data-testid={`leave-toggle-${l.id}`}
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); } }}
        className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-slate-50 transition-colors cursor-pointer"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {open ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
            <p className="font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{LEAVE_TYPE_LABEL[l.leave_type] || l.leave_type}</p>
            <StatusPill status={l.status} />
          </div>
          <p className="text-xs text-slate-600 ml-5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {fmtDate(l.start_date)} → {fmtDate(l.end_date)}{l.driver_name ? ` • ${l.driver_name}` : ''}
          </p>
          {l.reason && !open && <p className="text-xs text-slate-600 mt-1 ml-5 italic truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>"{l.reason}"</p>}
        </div>
        {isAdmin && l.status === 'pending' && (
          <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
            <button data-testid={`approve-leave-${l.id}`} onClick={() => onReview('approve')} className="p-1.5 bg-green-100 text-green-700 hover:bg-green-200 rounded-md" title="Approve">
              <Check className="w-4 h-4" />
            </button>
            <button data-testid={`reject-leave-${l.id}`} onClick={() => onReview('reject')} className="p-1.5 bg-red-100 text-red-700 hover:bg-red-200 rounded-md" title="Reject">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      {open && (
        <div>
          {l.reason && (
            <div className="px-4 pb-2 text-xs text-slate-600" data-testid={`leave-reason-${l.id}`}>
              <span className="font-semibold uppercase tracking-widest mr-2 text-slate-500">Reason:</span>"{l.reason}"
            </div>
          )}
          <RequestThread
            apiPath={`/leave-requests/${l.id}`}
            requestId={l.id}
            isAdmin={isAdmin}
            status={l.status}
            firstViewedAt={l.first_viewed_at}
            firstViewedByName={l.first_viewed_by_name}
            autoMarkViewed={isAdmin && l.status === 'pending'}
            chainState={l.chain_state}
            onLifecycleChange={onChange}
          />
        </div>
      )}
    </div>
  );
};

const AdvanceRow = ({ a, isAdmin, onReview, onChange }) => {
  const [open, setOpen] = useState(false);
  const isReturned = a.status === 'returned_for_revision';
  return (
    <div data-testid={`advance-${a.id}`} className={`bg-white border rounded-md ${isReturned ? 'border-orange-300' : 'border-slate-200'}`}>
      <div
        data-testid={`advance-toggle-${a.id}`}
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); } }}
        className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-slate-50 transition-colors cursor-pointer"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {open ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
            <p className="font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>${a.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            <StatusPill status={a.status} />
          </div>
          <p className="text-xs text-slate-600 ml-5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Requested {fmtDate(a.created_at)}{a.driver_name ? ` • ${a.driver_name}` : ''}
          </p>
          {a.reason && !open && <p className="text-xs text-slate-600 mt-1 ml-5 italic truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>"{a.reason}"</p>}
        </div>
        {isAdmin && a.status === 'pending' && (
          <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
            <button data-testid={`approve-advance-${a.id}`} onClick={() => onReview('approve')} className="p-1.5 bg-green-100 text-green-700 hover:bg-green-200 rounded-md" title="Approve">
              <Check className="w-4 h-4" />
            </button>
            <button data-testid={`reject-advance-${a.id}`} onClick={() => onReview('reject')} className="p-1.5 bg-red-100 text-red-700 hover:bg-red-200 rounded-md" title="Reject">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      {open && (
        <div>
          {a.reason && (
            <div className="px-4 pb-2 text-xs text-slate-600" data-testid={`advance-reason-${a.id}`}>
              <span className="font-semibold uppercase tracking-widest mr-2 text-slate-500">Reason:</span>"{a.reason}"
            </div>
          )}
          <RequestThread
            apiPath={`/cash-advances/${a.id}`}
            requestId={a.id}
            isAdmin={isAdmin}
            status={a.status}
            firstViewedAt={a.first_viewed_at}
            firstViewedByName={a.first_viewed_by_name}
            autoMarkViewed={isAdmin && a.status === 'pending'}
            chainState={a.chain_state}
            onLifecycleChange={onChange}
          />
        </div>
      )}
    </div>
  );
};

export default TimeOffAndAdvances;
